// <s-marquee> — seamless continuous scroll of its [data-marquee-track] content.
// Clones the track once for a gapless loop and animates with the Web Animations API.
// Respects prefers-reduced-motion (no animation); pauses on hover, focus, hidden tab, and a sticky
// explicit pause from a `--toggle` command button (WCAG 2.2.2). One component per file.
class SMarquee extends HTMLElement {
  #animation = null;
  #ac = null;
  #mql = null;
  #hover = false;
  #focus = false;
  #explicit = false;

  connectedCallback() {
    this.#ac = new AbortController();
    const signal = this.#ac.signal;
    this.#mql = window.matchMedia("(prefers-reduced-motion: reduce)");
    this.#mql.addEventListener("change", () => this.#setup(), { signal });

    this.addEventListener("pointerenter", () => { this.#hover = true; this.#sync(); }, { signal });
    this.addEventListener("pointerleave", () => { this.#hover = false; this.#sync(); }, { signal });
    this.addEventListener("focusin", () => { this.#focus = true; this.#sync(); }, { signal });
    this.addEventListener("focusout", (event) => {
      if (!this.contains(event.relatedTarget)) { this.#focus = false; this.#sync(); }
    }, { signal });
    document.addEventListener("visibilitychange", () => this.#sync(), { signal });

    this.addEventListener("command", (event) => {
      if (event.command !== "--toggle") return;
      this.#explicit = !this.#explicit;
      this.#updateButton(event.source);
      this.#sync();
    }, { signal });

    this.#setup();
  }

  disconnectedCallback() {
    this.#ac?.abort();
    this.#animation?.cancel();
  }

  #updateButton(button) {
    if (!button) return;
    button.setAttribute("aria-pressed", String(this.#explicit));
    const label = this.#explicit ? button.dataset.playLabel : button.dataset.pauseLabel;
    if (label) button.setAttribute("aria-label", label);
    button.querySelectorAll("[data-icon]").forEach((icon) => {
      icon.hidden = icon.dataset.icon !== (this.#explicit ? "play" : "pause");
    });
  }

  #sync() {
    if (!this.#animation) return;
    const paused = this.#hover || this.#focus || this.#explicit || document.hidden;
    if (paused) this.#animation.pause();
    else this.#animation.play();
  }

  #setup() {
    this.#animation?.cancel();
    this.#animation = null;

    const track = this.querySelector("[data-marquee-track]");
    if (!track || this.#mql.matches) return;

    // Ensure exactly two copies of the content so the loop is seamless; copies are hidden from AT.
    if (!track.dataset.cloned) {
      const clones = [...track.children].map((node) => {
        const clone = node.cloneNode(true);
        clone.setAttribute("aria-hidden", "true");
        clone.inert = true;
        return clone;
      });
      track.append(...clones);
      track.dataset.cloned = "true";
    }

    const distance = track.scrollWidth / 2;
    if (distance <= 0) return;

    const speed = parseFloat(this.dataset.speed) || 40;
    const durationMs = (distance / speed) * 1000;
    const rtl = getComputedStyle(this).direction === "rtl";
    const reverse = this.dataset.direction === "right";
    const end = rtl ? distance : -distance;
    const from = reverse ? end : 0;
    const to = reverse ? 0 : end;

    this.#animation = track.animate(
      [{ transform: `translateX(${from}px)` }, { transform: `translateX(${to}px)` }],
      { duration: durationMs, iterations: Infinity, easing: "linear" },
    );
    this.#sync();
  }
}

if (!customElements.get("s-marquee")) {
  customElements.define("s-marquee", SMarquee);
}
